import { Skeleton } from "@mui/material";
import classnames from "classnames";
import React, { useCallback, useEffect, useRef } from "react";
import ReactPlayer from "react-player";

import { playerWatched } from "../../apollo/typePolicies/LocalStorage";
import { useAppState } from "../../hooks/useAppState";
import { createTelemetryPayload } from "../../telemetry/TelemetryUtils";
import Captions from "../Captions/Captions";
import Controls from "../Controls/Controls";
import { NoAudioToast } from "./NoAudioToast";
import TapToUnmute from "./TapToUnmute";
import useVideo from "./useVideo";
import { CaptionsContainer, Container, PlayerContainer } from "./Video.elements";
import {
  executeWhenPlayerReady,
  formatTimestamp,
  updatePlayerWatchedPayload,
  updateStoredMediaTime,
} from "./videoHelpers";

export function Video(): React.ReactElement | null {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const playerWrapperRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLVideoElement>(null);
  const timerId = useRef<NodeJS.Timeout>(null);

  const { loading, appState } = useAppState();

  const {
    videoURL,
    videoTitle,
    isPlaying,
    handleVideoProgress,
    handleVideoDuration,
    handleVideoEnded,
    handleClickPreviousVideo,
    handleClickNextVideo,
    isAutoplayEnabled,
    handleClickAutoplay,
    handleClickPlayPause,
    progress,
    handleMouseUpProgressBar,
    handleMouseDownProgressBar,
    handleMouseOutProgressBar,
    handleChangeProgressBar,
    handleClickReplay,
    isFullscreen,
    handleClickFullscreen,
    isPIPEnabled,
    handleClickPIP,
    playbackRate,
    handleClickPlaybackRate,
    volume,
    handleClickMuteVolume,
    handleChangeVolumeSlider,
    duration,
    handleVideoReady,
    handleError,
    captionsURL,
    handleClickCaptions,
    captionsEnabled,
    handleChangeView,
    controlsVisible,
    showControls,
    hasNoAudio,
    muted,
    setMuted,
    handleClickVideoContainer,
    hasCustomDuration,
    setPlayAfterSeek,
    skip,
    tapToUnmute,
    setTapToUnmute,
    loading: videoLoading,
    setBuffering,
    setControlsVisible,
    captionsText,
    captionLanguage,
    setCaptionLanguage,
    disableSliderTransition,
    videoEndedRef,
    isGeoBlocked,
    isCheckingAccess,
  } = useVideo(wrapperRef.current, playerWrapperRef.current, playerRef.current);

  const handleBufferEnd = () => setBuffering(false);

  const handleTouchMove = () => {
    if (timerId.current) {
      clearTimeout(timerId.current);
    }
    setPlayAfterSeek(true);
    showControls();

    timerId.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  };

  const handleMouseLeave = () => setControlsVisible(false);

  const handleTapToUnmute = () => {
    setMuted(false);
    setTapToUnmute(false);
  };

  const handleMouseMove = useCallback(() => {
    if (timerId.current) {
      clearTimeout(timerId.current);
    }
    showControls();

    timerId.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  }, [showControls, setControlsVisible]);

  const containerClassnames = classnames({
    fullscreen: isFullscreen,
    "captions-enabled": captionsEnabled && captionsURL,
  });

  const videoContainerClassnames = classnames({
    fullscreen: isFullscreen,
  });

  useEffect(() => {
    const container = wrapperRef.current;
    container?.addEventListener("mousemove", handleMouseMove);

    return () => {
      container?.removeEventListener("mousemove", handleMouseMove);
      if (timerId.current) {
        clearTimeout(timerId.current);
      }
    };
  }, [handleMouseMove]);

  // Custom progress update interval for smooth progress bar updates
  useEffect(() => {
    if (!isPlaying || !playerRef.current) return;

    const interval = setInterval(() => {
      // Don't update if video ended
      if (videoEndedRef.current) {
        return;
      }

      const video = playerRef.current;
      if (video?.duration && isFinite(video.duration)) {
        const progressState = {
          played: video.currentTime / video.duration,
          playedSeconds: video.currentTime,
          loaded: video.buffered.length > 0 ? video.buffered.end(video.buffered.length - 1) / video.duration : 0,
          loadedSeconds: video.buffered.length > 0 ? video.buffered.end(video.buffered.length - 1) : 0,
        };
        handleVideoProgress(progressState);
      }
    }, 100); // 100ms interval for smooth updates

    return () => clearInterval(interval);
  }, [isPlaying, handleVideoProgress, videoEndedRef]);

  // Function to add watched second to local storage with telemetry payload
  const addWatchedSecond = useCallback((slideUuid: string, second: number) => {
    const currentData = playerWatched();

    // Get existing slide data or initialize
    const existingSlideData = currentData[slideUuid];
    const existingSeconds = existingSlideData?.seconds || [];

    // Prevent the specific case of adding 0 when array already contains [0]
    if (existingSeconds.length === 1 && existingSeconds[0] === 0 && second === 0) {
      return;
    }

    // Calculate metrics for action_meta
    const video = playerRef.current;
    const duration = video?.duration ? Math.floor(video.duration) : 0;
    const updatedSeconds = [...existingSeconds, second];
    const uniqueSeconds = [...new Set(updatedSeconds)];
    const uniqueContentWatched = Math.max(0, uniqueSeconds.length - 1);
    const totalContentWatched = Math.max(0, updatedSeconds.length - 1);
    const percentageWatched = duration > 0 ? ((uniqueContentWatched / duration) * 100).toFixed(1) : "0.0";

    const actionMeta =
      `duration: ${formatTimestamp(duration)}, ` +
      `unique content watched: ${formatTimestamp(uniqueContentWatched)} (${percentageWatched}%), ` +
      `total content watched: ${formatTimestamp(totalContentWatched)}`;

    // Always create/update the payload with current state
    const payload = createTelemetryPayload({
      feature: "playback",
      action: "video watched",
      action_meta: actionMeta,
    });

    updatePlayerWatchedPayload(currentData, slideUuid, payload, updatedSeconds);
  }, []);

  // Function to track current watched second
  const trackWatchedSecond = useCallback(() => {
    const video = playerRef.current;
    if (video?.duration && isFinite(video.duration) && appState.slideId) {
      const currentSecond = Math.floor(video.currentTime);
      addWatchedSecond(appState.slideId, currentSecond);
    }
  }, [appState.slideId, addWatchedSecond]);

  // Function to update media_time only on local storage item
  const handleUpdateStoredMediaTime = useCallback((slideUuid: string) => {
    updateStoredMediaTime(slideUuid, playerRef as React.RefObject<HTMLVideoElement>);
  }, []);

  // Track watched seconds for telemetry
  useEffect(() => {
    if (!isPlaying || !appState.slideId) {
      return;
    }

    let lastSecondTracked = -1;

    // Track immediately when playback starts (if player is ready)
    const initialSecond = executeWhenPlayerReady(playerRef as React.RefObject<HTMLVideoElement>, () => {
      trackWatchedSecond();
    });
    if (initialSecond !== undefined) {
      lastSecondTracked = initialSecond;
    }

    // Track every 100ms to keep media_time current, but only add new seconds when they change
    const interval = setInterval(() => {
      executeWhenPlayerReady(playerRef as React.RefObject<HTMLVideoElement>, currentSecond => {
        // Only track new seconds (this happens roughly every 1000ms)
        if (currentSecond !== lastSecondTracked) {
          trackWatchedSecond();
          lastSecondTracked = currentSecond;
        } else if (appState.slideId) {
          // Update stored payload with current media_time more frequently (every 100ms)
          handleUpdateStoredMediaTime(appState.slideId);
        }
      });
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [isPlaying, appState.slideId, trackWatchedSecond, handleUpdateStoredMediaTime]);

  if (!videoURL || loading.module || isGeoBlocked || isCheckingAccess) {
    return <Skeleton variant="rectangular" sx={{ height: "100%", aspectRatio: "16/9" }} />;
  }

  return (
    <>
      <NoAudioToast show={hasNoAudio} />
      <Container
        sx={{
          cursor: controlsVisible ? "initial" : "none",
        }}
        ref={wrapperRef}
        onMouseMove={handleMouseMove}
        onFocus={showControls}
        onTouchMove={handleTouchMove}
        onMouseLeave={handleMouseLeave}
        id="video-player"
        data-testid="video-player"
        className={videoContainerClassnames}
      >
        {tapToUnmute && <TapToUnmute onClick={handleTapToUnmute} />}

        <Controls
          videoTitle={videoTitle}
          isPlaying={isPlaying}
          handleClickPreviousVideo={handleClickPreviousVideo}
          handleClickNextVideo={handleClickNextVideo}
          isAutoplayEnabled={isAutoplayEnabled}
          handleClickAutoplay={handleClickAutoplay}
          handleClickPlayPause={handleClickPlayPause}
          progress={progress}
          handleMouseUpProgressBar={handleMouseUpProgressBar}
          handleMouseDownProgressBar={handleMouseDownProgressBar}
          handleMouseOutProgressBar={handleMouseOutProgressBar}
          handleChangeProgressBar={handleChangeProgressBar}
          handleClickReplay={handleClickReplay}
          isFullscreen={isFullscreen}
          handleClickFullscreen={handleClickFullscreen}
          handleClickPIP={handleClickPIP}
          playbackRate={playbackRate}
          handleClickPlaybackRate={handleClickPlaybackRate}
          volume={volume > 1 ? 0.5 : volume}
          handleClickMuteVolume={handleClickMuteVolume}
          handleChangeVolumeSlider={handleChangeVolumeSlider}
          duration={duration}
          handleClickCaptions={handleClickCaptions}
          captionsEnabled={captionsEnabled}
          handleChangeView={handleChangeView}
          visible={controlsVisible}
          muted={muted}
          hasCustomDuration={hasCustomDuration}
          skip={skip}
          handleClickVideoContainer={handleClickVideoContainer}
          loading={videoLoading}
          captionLanguage={captionLanguage}
          setCaptionLanguage={setCaptionLanguage}
          disableSliderTransition={disableSliderTransition}
        />

        <PlayerContainer
          ref={playerWrapperRef}
          id="player-container"
          className={containerClassnames}
          data-muted={muted}
        >
          <ReactPlayer
            pip={isPIPEnabled}
            src={videoURL}
            ref={playerRef}
            width="100%"
            height="100%"
            preload="auto"
            playbackRate={playbackRate}
            volume={volume > 1 ? 0.5 : volume}
            playing={isPlaying}
            onDurationChange={event => {
              const video = event.currentTarget;
              handleVideoDuration(video.duration);
            }}
            onEnded={handleVideoEnded}
            onCanPlay={handleVideoReady}
            onWaiting={() => isPlaying && setBuffering(true)}
            onPlaying={handleBufferEnd}
            onError={handleError}
            muted={muted}
            config={{
              hls: {
                progressive: true,
                startFragPrefetch: true,
                xhrSetup: function (xhr: { withCredentials: boolean }) {
                  xhr.withCredentials = true; // send cookies
                },
              },
            }}
          />
        </PlayerContainer>
        {captionsEnabled && (
          <CaptionsContainer id="captions-container">
            <Captions text={captionsText} />
          </CaptionsContainer>
        )}
      </Container>
    </>
  );
}
